import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Helmet } from 'react-helmet';
import {
  Icon,
  SvgBuilding2Bold,
  SvgCalendarBold,
  SvgHyperlink2Bold,
  SvgMultipleUsers2Bold,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useAuth } from 'contexts/AuthContext';
import useIsAboveMobile from 'components/hooks/useIsAboveMobile';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import DashboardMarketingPanel from 'components/Marketing/DashboardMarketingPanel';
import QuickActions from './QuickActions';
import DashboardCard from './DashboardCard';
import { FAQsection } from './FAQsection';
import { ProfileAccess } from './ProfileAccess';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const aboveMobile = useIsAboveMobile('md');

  return (
    <>
      <Helmet>
        <title>{t('ui.event-management.title.dashboard')}</title>
      </Helmet>
      <Container
        maxWidth="lg"
        sx={(theme) => ({
          marginTop: theme.spacing(spacingSizeMap.L),
          marginBottom: theme.spacing(spacingSizeMap.L),
        })}
      >
        <TextV2 component="h1" variant="h2">
          {t('ui.event-management.dashboard.heading', {
            fullName: user?.fullName,
          })}
        </TextV2>
        <Grid container>
          <Grid size={{ xs: 12, md: 8 }}>
            <Grid
              container
              spacing={2}

              columns={12}
              sx={[
                {
                  flexDirection: aboveMobile ? 'row' : 'column-reverse',
                  justifyContent: 'space-between',
                },
                (theme) => ({
                  marginTop: `calc(${theme.spacing(spacingSizeMap.L)} + 8px)`,
                }),
              ]}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  href="/experiences"
                  Icon={
                    <Icon
                      icon={<SvgCalendarBold />}
                      iconColour="pink"
                      iconShape="square"
                    />
                  }
                  textPrimary={t(
                    'ui.event-management.dashboard.card-header.experience',
                  )}
                  textSecondary={t(
                    'ui.event-management.dashboard.card-body.experience',
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  href="/organisations"
                  Icon={
                    <Icon
                      icon={<SvgBuilding2Bold />}
                      iconColour="orange"
                      iconShape="square"
                    />
                  }
                  textPrimary={t(
                    'ui.event-management.dashboard.card-header.organisations',
                  )}
                  textSecondary={t(
                    'ui.event-management.dashboard.card-body.organisations',
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  href="/learners"
                  Icon={
                    <Icon
                      icon={<SvgMultipleUsers2Bold />}
                      iconColour="green"
                      iconShape="square"
                    />
                  }
                  textPrimary={t(
                    'ui.event-management.dashboard.card-header.learners',
                  )}
                  textSecondary={t(
                    'ui.event-management.dashboard.card-body.learners',
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  href="/evaluator-links"
                  Icon={
                    <Icon
                      icon={<SvgHyperlink2Bold />}
                      iconColour="blue"
                      iconShape="square"
                    />
                  }
                  textPrimary={t(
                    'ui.event-management.dashboard.card-header.evaluator-links',
                  )}
                  textSecondary={t(
                    'ui.event-management.dashboard.card-body.evaluator-links',
                  )}
                />
              </Grid>
            </Grid>
            <Grid size={{ md: 12 }}>
              <ProfileAccess />
            </Grid>
            <Grid size={{ md: 12 }}>
              <FAQsection />
            </Grid>
          </Grid>
          <Grid
            sx={(theme) => ({
              padding: theme.spacing(spacingSizeMap.M),
              paddingLeft: theme.spacing(spacingSizeMap.L),
              marginTop: theme.spacing(spacingSizeMap.S),
            })}
            size={{ xs: 12, md: 4 }}
          >
            <DashboardMarketingPanel />
            <Grid>
              <QuickActions />
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Dashboard;
